import React from "react";
import { Reveal } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { EVENT_SERVICES, IMAGES, SITE, waLink } from "@/data/site";
import { MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

export default function Events() {
  const eventTypes = [
    "Puja / Ceremony", "Wedding", "Destination Wedding", "Engagement", "Haldi / Mehndi / Sangeet",
    "Birthday", "Anniversary", "Corporate Event", "Conference / MICE", "Other",
  ];

  return (
    <div className="bg-[#361E02] text-[#F9EFE1]">
      <Seo title="Event Management" description="VacanzaGo's event management vertical — puja décor to destination weddings, corporate events, makeup, photography, catering and décor. You bring the occasion, we handle the experience." />
      {/* Hero */}
      <div className="relative h-[75vh] min-h-[500px]">
        <img src={IMAGES.eventMandap} alt="Luxury wedding mandap décor" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#361E02]/70" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 lg:px-12">
            <Reveal className="max-w-2xl">
              <p className="eyebrow text-[#D4AF37]">Event Management — a new vertical by VacanzaGo</p>
              <h1 className="font-display text-5xl sm:text-7xl font-medium tracking-tight mt-5 leading-[1.02] text-white">
                You bring the occasion.<br />We handle the experience.
              </h1>
              <p className="mt-6 text-lg text-[#F9EFE1]/85">
                From an intimate puja to a full destination wedding or a complete corporate event —
                one team, every detail. Enquire for a tailored quote.
              </p>
              <a href={waLink("Hi Vacanza Go, I'd like to plan an event. Please share details.")} target="_blank" rel="noreferrer" data-testid="events-hero-wa" className="inline-flex items-center gap-2 mt-8 rounded-full btn-orange px-8 py-3.5 text-sm">
                <MessageCircle size={16} /> Plan an Event
              </a>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Services */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl mb-14">
            <p className="eyebrow text-[#D4AF37]">Full-service, end to end</p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-3 text-white">Every part of the celebration</h2>
            <p className="mt-4 text-[#F9EFE1]/75">Whether it's a simple puja décor or a whole destination wedding package — we do it all, with the same premium care.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {EVENT_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 70}>
                <div className="h-full rounded-md border border-white/10 bg-white/[0.03] p-7 hover:border-[#F49E1A]/40 transition-colors" data-testid={`event-service-${i}`}>
                  <span className="font-display text-2xl text-[#D4AF37]">0{i + 1 <= 9 ? i + 1 : i + 1}</span>
                  <h3 className="font-display text-2xl text-white mt-3">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#F9EFE1]/70 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Explore by occasion */}
      <section className="pb-4 pt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <p className="eyebrow text-[#D4AF37] mb-6">Explore by occasion</p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { to: "/events/weddings", l: "Weddings" },
              { to: "/events/destination-weddings", l: "Destination Weddings" },
              { to: "/events/celebrations", l: "Celebrations" },
              { to: "/events/puja-religious", l: "Puja & Religious" },
              { to: "/events/corporate-events", l: "Corporate Events" },
            ].map((e) => (
              <Link key={e.to} to={e.to} data-testid={`event-type-${e.to.split("/").pop()}`}
                className="rounded-md border border-white/12 bg-white/[0.03] px-5 py-4 text-center hover:border-[#F49E1A]/50 transition-colors">
                <span className="font-display text-lg text-white">{e.l}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery strip */}
      <section className="pb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[IMAGES.eventFloral, IMAGES.eventCandles, IMAGES.eventDining, IMAGES.eventMandap].map((img, i) => (
            <div key={img} className="img-zoom aspect-square overflow-hidden">
              <img src={img} alt="Event décor" className="h-full w-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* Inquiry */}
      <section className="py-20 lg:py-28 bg-[#FFF7EC] text-[#2A1808]">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <p className="eyebrow">Enquire for event curation</p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-3">Tell us about your occasion</h2>
            <p className="mt-4 text-[#553204]">Share a few details and we'll come back with ideas and an itemised quote — no obligation.</p>
            <div className="mt-8">
              <LeadForm
                source="event"
                buttonLabel="Send event enquiry"
                waPrefix="Hi Vacanza Go, I'd like to plan an event."
                extraFields={{
                  initial: { event_type: "", event_date: "", guests: "" },
                  keys: ["event_type", "event_date", "guests"],
                  render: (form, upd, input) => (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <select data-testid="event-type" className={input} value={form.event_type} onChange={upd("event_type")}>
                        <option value="">Occasion type</option>
                        {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <input data-testid="event-date" className={input} placeholder="Approx date" value={form.event_date} onChange={upd("event_date")} />
                      <input data-testid="event-guests" className={input} placeholder="Guests (approx)" value={form.guests} onChange={upd("guests")} />
                    </div>
                  ),
                }}
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
